<script setup>
import {
  useViewVehicles,
  useGenerateVehicleBill,
  useDownloadVehicleSticker,
  useBulkDownloadVehicleStickers,
  useSubmitArchivalApplication,
} from "./view_vehicles_logic";
import ViewVehiclesTable from "./ViewVehiclesTable.vue";
import ViewVehicle from "./ViewVehicle.vue";
import ApplyForArchival from "./ApplyForArchival.vue";
import ViewArchivalApplications from "./ViewArchivalApplications.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import { computed, ref } from "vue";
import { useRouterFromInstance, useDownloadFile } from "@/utils/vue_helpers";
import {
  resetSnackbarOnMount,
  snackbarData,
} from "@/components/utils/snackbar/logic";
import LoadingArea from "../utils/LoadingArea.vue";
import Pagination from "../utils/Pagination.vue";
import PayForVehicle from "./PayForVehicle.vue";
import { routes } from "@/router/routes";
import {
  downloadBlob,
  getVehicleSticker,
  vehicleStickerExpired,
} from "@/utils/functions";
import FilterValues from "../utils/FilterValues.vue";
import FilterDialog from "../utils/FilterDialog.vue";
import FilterButton from "../utils/FilterButton.vue";

resetSnackbarOnMount();

const { vehiclesData, searchModel, filters } = useViewVehicles();

// ── Page navigation ──────────────────────────────────────────────────────────
const pages = {
  VIEW_VEHICLES_TABLE: 0,
  VIEW_VEHICLE_DETAILS: 1,
  VIEW_PAY_FOR_VEHICLE: 2,
};
const currentPage = ref(pages.VIEW_VEHICLES_TABLE);

// ── Top-level tabs (Vehicles list | Archival Applications) ───────────────────
const vehicleTab = ref(0);

// ── Vehicle under view ────────────────────────────────────────────────────────
const vehicleUnderView = ref(null);
const vehicleUnderViewNotEmpty = computed(() => vehicleUnderView.value !== null);

function moveToViewVehiclePage(vehicle) {
  vehicleUnderView.value = vehicle;
  currentPage.value = pages.VIEW_VEHICLE_DETAILS;
}

function moveToViewVehicleTable() {
  currentPage.value = pages.VIEW_VEHICLES_TABLE;
}

// ── Pay for vehicle ───────────────────────────────────────────────────────────
const vehiclesToPayFor = ref([]);
function onPayForVehiclesClicked(vehicles) {
  vehiclesToPayFor.value = vehicles;
  currentPage.value = pages.VIEW_PAY_FOR_VEHICLE;
}

const generateVehicleBill = useGenerateVehicleBill();
const { router } = useRouterFromInstance();
function onGoToPaymentSectionClicked({ period, remarks, subCountyId, vehicles }) {
  const firstVehicle = vehicles[0];
  const vehicleAccount = firstVehicle.account;
  const vehicleIds = vehicles.map((vehicle) => vehicle.id);
  generateVehicleBill.mutate(
    { vehicleAccountNumber: vehicleAccount.accountNo, createdBy: vehicleAccount.created_by, remarks, period, subCountyId, vehicleIds },
    {
      onSuccess(bill) {
        snackbarData.text = "Vehicle Bill Generated";
        snackbarData.color = "success";
        snackbarData.open = true;
        const paymentRoute = routes.payment.withQuery({
          billNumbersToPayAsString: bill.bill_number,
          directPayment: true,
          billsToPay: [bill],
        });
        router.push(paymentRoute);
      },
    }
  );
}

// ── Download sticker ──────────────────────────────────────────────────────────
const { downloadVehicleStickerMutation: downloadVehicleSticker } = useDownloadVehicleSticker();

function onDownloadVehicleStickerClicked(vehicle) {
  const sticker = getVehicleSticker(vehicle);
  const hasNoSticker = !!!sticker;
  if (hasNoSticker) {
    snackbarData.text = "No Sticker Found";
    snackbarData.color = "error";
    snackbarData.open = true;
    return;
  }
  if (vehicleStickerExpired(sticker)) {
    snackbarData.text = "Sticker Expired";
    snackbarData.color = "error";
    snackbarData.open = true;
    return;
  }
  snackbarData.text = "Downloading Vehicle Sticker";
  snackbarData.color = "success";
  snackbarData.open = true;
  downloadVehicleSticker.mutate(
    { vehicle },
    {
      onSuccess(data) {
        downloadBlob({ blob: data, filename: "vehicle-sticker" });
      },
    }
  );
}

// ── Bulk download stickers ────────────────────────────────────────────────────
const filterDialogOpen = ref(false);
const { downloadFile } = useDownloadFile();
function onDownloadVehicleListClicked() {
  downloadFile({ shouldDownloadVehicleList: true });
}

const { bulkDownloadVehicleStickersMutation: bulkDownloadVehicleSticker } =
  useBulkDownloadVehicleStickers();

function onBulkDownloadVehicleStickersClicked(vehicles) {
  let atLeastOneValidSticker = false;
  for (const selectedVehicle of vehicles) {
    const sticker = getVehicleSticker(selectedVehicle);
    if (!vehicleStickerExpired(sticker)) {
      atLeastOneValidSticker = true;
      break;
    }
  }
  if (!atLeastOneValidSticker) {
    snackbarData.text = "All selected vehicles have expired";
    snackbarData.color = "error";
    snackbarData.open = true;
    return;
  }
  const vehicleIds = vehicles.map((vehicle) => vehicle.id);
  snackbarData.text = "Downloading Bulk Vehicle Stickers";
  snackbarData.color = "success";
  snackbarData.open = true;
  bulkDownloadVehicleSticker.mutate({ vehicleIds });
}

// ── Archival Application ──────────────────────────────────────────────────────
const archivalDialogOpen = ref(false);
const vehicleForArchival = ref(null);
const submitArchivalMutation = useSubmitArchivalApplication();

function onApplyForArchivalClicked(vehicle) {
  vehicleForArchival.value = vehicle;
  archivalDialogOpen.value = true;
}

function onArchivalSubmit({ vehicleId, remarks }) {
  submitArchivalMutation.mutate(
    { vehicleId, remarks },
    {
      onSuccess() {
        archivalDialogOpen.value = false;
        vehicleForArchival.value = null;
        snackbarData.text =
          "Archival application submitted successfully! It is now pending officer review.";
        snackbarData.color = "success";
        snackbarData.open = true;
        // Return to vehicle list so client sees updated status
        moveToViewVehicleTable();
      },
      onError(error) {
        const msg =
          error?.response?.data?.message ||
          "Failed to submit archival application. Please try again.";
        snackbarData.text = msg;
        snackbarData.color = "error";
        snackbarData.open = true;
      },
    }
  );
}
</script>

<template>
  <v-container fluid class="pa-0">

    <!-- Top-level tab bar: My Vehicles | My Archival Applications -->
    <v-tabs v-model="vehicleTab" color="primary" class="mb-3">
      <v-tab>
        <v-icon left small>mdi-car</v-icon>
        My Vehicles
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-archive-clock</v-icon>
        Archival Applications
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="vehicleTab" touchless>
      <!-- ── TAB 0: My Vehicles ──────────────────────────────────────────── -->
      <v-tab-item>
        <v-tabs-items touchless v-model="currentPage">
          <!-- Vehicles table -->
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <div class="text-h6 text-center font-weight-bold primary--text">
                  Vehicles
                </div>
              </v-col>
            </v-row>
            <v-row justify-md="center" align-md="end" dense>
              <v-col cols="3" class="d-md-flex justify-md-end">
                <FilterButton @click="filterDialogOpen = true"> </FilterButton>
              </v-col>
              <v-col cols="12" md="6">
                <LabelledField label="Search">
                  <v-text-field
                    class="background text-field"
                    outlined
                    dense
                    v-model="searchModel"
                    clearable
                  />
                </LabelledField>
              </v-col>
              <v-col cols="3" />
            </v-row>
            <LoadingArea :loading="vehiclesData.fetchingData" class="mt-4">
              <v-row dense>
                <v-col cols="12">
                  <FilterValues
                    :filterValuesForDisplay="filters.filterValuesForDisplay.value"
                    :clear-all-filters="filters.clearAllFilters"
                    :at-least-one-filter-active="filters.atLeastOneFilterActive.value"
                    showDivider
                  ></FilterValues>
                </v-col>
              </v-row>

              <v-row class="no-gutters">
                <v-col cols="12">
                  <ViewVehiclesTable
                    :vehicles="vehiclesData.fetchedData"
                    :loading="vehiclesData.fetchingData"
                    :bulkDownloadingStickers="bulkDownloadVehicleSticker.isLoading.value"
                    @viewVehicle="moveToViewVehiclePage"
                    @downloadSticker="onDownloadVehicleStickerClicked"
                    @payForVehiclesClicked="onPayForVehiclesClicked"
                    @downloadVehicleListClicked="onDownloadVehicleListClicked"
                    @bulkDownloadStickersClicked="onBulkDownloadVehicleStickersClicked"
                    @applyForArchival="onApplyForArchivalClicked"
                  >
                  </ViewVehiclesTable>
                </v-col>
              </v-row>
              <v-row justify="end">
                <v-col cols="auto">
                  <Pagination
                    :current-page="vehiclesData.paginationData.currentPage"
                    :total-number-of-items="vehiclesData.paginationData.total"
                    :items-per-page="vehiclesData.paginationData.perPage"
                    @paginationClicked="vehiclesData.setNewPage($event)"
                  >
                  </Pagination>
                </v-col>
              </v-row>
            </LoadingArea>
          </v-tab-item>

          <!-- Vehicle details -->
          <v-tab-item :value="pages.VIEW_VEHICLE_DETAILS">
            <v-row>
              <v-col cols="12">
                <ViewVehicle
                  v-if="vehicleUnderViewNotEmpty"
                  :vehicle="vehicleUnderView"
                  showGoBack
                  @downloadSticker="onDownloadVehicleStickerClicked"
                  @goBackClicked="moveToViewVehicleTable"
                  @applyForArchival="onApplyForArchivalClicked"
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Pay for vehicle -->
          <v-tab-item :value="pages.VIEW_PAY_FOR_VEHICLE">
            <v-row>
              <v-col cols="12">
                <PayForVehicle
                  :vehicles="vehiclesToPayFor"
                  :generatingVehicleBill="generateVehicleBill.isLoading.value"
                  @goBackClicked="moveToViewVehicleTable"
                  @payVehiclesClicked="onGoToPaymentSectionClicked"
                ></PayForVehicle>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-tab-item>

      <!-- ── TAB 1: Archival Applications ──────────────────────────────────── -->
      <v-tab-item>
        <ViewArchivalApplications />
      </v-tab-item>
    </v-tabs-items>

    <!-- Filter dialog -->
    <FilterDialog
      :filters="filters.filtersRef.value"
      :loading="vehiclesData.fetchingData"
      :dialogOpen.sync="filterDialogOpen"
      :clear-all-filters="filters.clearAllFilters"
      :at-least-one-filter-active="filters.atLeastOneFilterActive.value"
    />

    <!-- Archival Application Dialog -->
    <ApplyForArchival
      :open.sync="archivalDialogOpen"
      :vehicle="vehicleForArchival"
      :loading="submitArchivalMutation.isLoading.value"
      @submit="onArchivalSubmit"
    />
  </v-container>
</template>
